import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";

import { useGetOrganizations } from "../queries/organization";
import { useGetProcurements } from "../queries/procurement";
import { formatOrganizationLabel } from "../services/organization";
import {
  ProcurementFormOrganization,
  uniqueSortedTagSuggestions,
} from "../services/procurement";
import { useLocale } from "../stores/locale";
import { recordIdToId } from "../utils/record-id-to-id";
import { Button } from "./button";
import { Input } from "./input";
import { Select } from "./select";
import { TagInput } from "./tag-input";

export type ProcurementFormValues = {
  title: string;
  description: string;
  tags: string[];
  organizations: ProcurementFormOrganization[];
};

export function ProcurementForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: ProcurementFormValues) => void;
  onSubmit: () => void;
  values: ProcurementFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: organizations } = useGetOrganizations();
  const { data: procurements } = useGetProcurements();
  const [draftOrganizationId, setDraftOrganizationId] = useState("");
  const [draftPrice, setDraftPrice] = useState("");

  const tagSuggestions = useMemo(
    () => uniqueSortedTagSuggestions(procurements),
    [procurements],
  );

  const selectedOrganizationIds = useMemo(
    () =>
      new Set(
        values.organizations.map((organization) => organization.organizationId),
      ),
    [values.organizations],
  );

  const organizationOptions = useMemo(
    () =>
      (organizations ?? [])
        .filter(
          (organization) =>
            !selectedOrganizationIds.has(recordIdToId(organization.id)),
        )
        .map((organization) => ({
          label: formatOrganizationLabel(organization),
          value: recordIdToId(organization.id),
        })),
    [organizations, selectedOrganizationIds],
  );

  const selectedOrganizations = useMemo(
    () =>
      values.organizations.map((organization) => {
        const match = organizations?.find(
          (candidate) =>
            recordIdToId(candidate.id) === organization.organizationId,
        );
        return {
          ...organization,
          label: match
            ? formatOrganizationLabel(match)
            : organization.organizationId,
        };
      }),
    [organizations, values.organizations],
  );

  const isValid = useMemo(() => {
    return values.title.trim() !== "";
  }, [values.title]);

  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  const addOrganization = () => {
    if (
      draftOrganizationId === "" ||
      selectedOrganizationIds.has(draftOrganizationId)
    ) {
      return;
    }

    onChange({
      ...values,
      organizations: [
        ...values.organizations,
        {
          organizationId: draftOrganizationId,
          price: draftPrice,
        },
      ],
    });
    setDraftOrganizationId("");
    setDraftPrice("");
  };

  const removeOrganization = (index: number) => {
    onChange({
      ...values,
      organizations: values.organizations.filter(
        (_, organizationIndex) => organizationIndex !== index,
      ),
    });
  };

  const updateOrganization = (
    index: number,
    changes: Partial<ProcurementFormOrganization>,
  ) => {
    onChange({
      ...values,
      organizations: values.organizations.map(
        (organization, organizationIndex) =>
          organizationIndex === index
            ? { ...organization, ...changes }
            : organization,
      ),
    });
  };

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Input
          required
          className="col-span-2"
          disabled={isDisabled}
          label={t.procurement.title}
          type="text"
          value={values.title}
          onChange={(e) => onChange({ ...values, title: e.target.value })}
        />
        <div className="col-span-2">
          <label
            className={`block text-sm font-medium ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.procurement.description}
          </label>
          <textarea
            className="w-full border-1 border-gray-300 rounded-xs p-2 min-h-[4.5rem] bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            disabled={isDisabled}
            value={values.description}
            onChange={(e) =>
              onChange({ ...values, description: e.target.value })
            }
          />
        </div>
        <div className="col-span-2 border-t border-gray-200 mt-2 pt-3">
          <label
            className={`block text-sm font-medium ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.organization.organizations}
          </label>

          <div className="mt-2 border-1 border-gray-300 rounded-xs p-2 bg-white disabled:bg-gray-100">
            {selectedOrganizations.length === 0 && (
              <p className="text-sm text-gray-500">
                {t.procurement.organizationsEmpty}
              </p>
            )}
            {selectedOrganizations.map((organization, index) => (
              <div
                key={`${organization.organizationId}-${index}`}
                className="grid grid-cols-[1fr_8rem_auto] gap-2 items-end py-1"
              >
                <p className="text-sm text-gray-700 truncate pb-2">
                  {organization.label}
                </p>
                <Input
                  disabled={isDisabled}
                  label={t.procurement.organizationPrice}
                  suffix="kr"
                  type="number"
                  value={organization.price}
                  onChange={(e) =>
                    updateOrganization(index, { price: e.target.value })
                  }
                />
                <Button
                  className="mb-0.5"
                  disabled={isDisabled}
                  severity="secondary"
                  size="sm"
                  type="button"
                  onClick={() => removeOrganization(index)}
                >
                  {t.procurement.organizationRemove}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-2 grid grid-cols-[1fr_8rem_auto] gap-2 items-end">
            <Select
              allowEmpty
              disabled={isDisabled || organizationOptions.length === 0}
              label={t.organization.organization}
              options={organizationOptions}
              placeholder={t.select.placeholder}
              value={draftOrganizationId}
              onChange={setDraftOrganizationId}
            />
            <Input
              disabled={isDisabled}
              label={t.procurement.organizationPrice}
              suffix="kr"
              type="number"
              value={draftPrice}
              onChange={(e) => setDraftPrice(e.target.value)}
            />
            <Button
              disabled={isDisabled || draftOrganizationId === ""}
              size="sm"
              type="button"
              onClick={addOrganization}
            >
              {t.procurement.organizationAdd}
            </Button>
          </div>
        </div>
        <div className="col-span-2 w-full">
          <TagInput
            disabled={isDisabled}
            hint={t.procurement.tagsHint}
            label={t.procurement.tags}
            placeholder={t.procurement.tagsPlaceholder}
            suggestions={tagSuggestions}
            value={values.tags}
            onChange={(tags) => onChange({ ...values, tags })}
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end mt-4">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.procurement.error}
          </p>
        )}
        <Button disabled={disabled} severity="secondary" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={disabled || !isValid} loading={loading} type="submit">
          {t.page.save}
        </Button>
      </div>
    </form>
  );
}
